import {
  OperationStatusLabel,
  ReceivedInvoiceAiSuggestStatusProperty,
  TimestampStatusLabel,
} from '@/context/services/invoice/type';
import { useAiSuggestStatusChecker } from '@/features/invoices/components/Details/Components/Context/AiSuggestStatus';
import { useGetErrorMessages } from '@/features/invoices/components/Details/Components/Context/ErrorMessagesProvider';
import { useOperationStatusChecker } from '@/features/invoices/components/Details/Components/Context/OperationStatus';
import { useTimestampStatusChecker } from '@/features/invoices/components/Details/Components/Context/TimestampStatus';
import {
  hasAiSuggestResult,
  isAiSuggestSuccess,
} from '@/features/invoices/utils/aiSuggestStatus';
import { useTranslation } from '@/i18n';
import { SearchReceivedInvoicesResponseReceivedInvoicesItemAllOfErrorMessagesItemMessageType } from 'ap-openapi';
import { useMemo } from 'react';
import { StatusTagInfo } from '../components/ProcessingStatusTag';

export const useProcessingStatusTags = (
  aiSuggestStatus?: ReceivedInvoiceAiSuggestStatusProperty
) => {
  const { t } = useTranslation();
  const operationChecker = useOperationStatusChecker();
  const timestampChecker = useTimestampStatusChecker();
  const aiSuggestChecker = useAiSuggestStatusChecker(aiSuggestStatus);
  const { getErrorMessage } = useGetErrorMessages();
  const eDocScannerErrorMessage = getErrorMessage(
    SearchReceivedInvoicesResponseReceivedInvoicesItemAllOfErrorMessagesItemMessageType.edoc_scanner_message
  );
  const statusTagsInfo = useMemo(() => {
    if (!operationChecker && !timestampChecker) return [];

    const tags: StatusTagInfo[] = [];

    // OCR Processing status
    if (operationChecker?.isOcrProcessing) {
      tags.push({
        status: 'success',
        label:
          OperationStatusLabel.ocr_processing ||
          t('invoice_ocr_processing_notification_message'),
        customIcon: null, // Will be added in FileNameCell
      });
    }

    // Timestamp Creating status - only show if E_DOC_METADATA flag is enabled
    if (timestampChecker?.isCreating) {
      tags.push({
        status: 'success',
        label:
          TimestampStatusLabel.creating ||
          t('invoice_timestamp_creating_notification_message'),
        customIcon: null, // Will be added in FileNameCell
      });
    }

    if (eDocScannerErrorMessage) {
      tags.push({
        status: eDocScannerErrorMessage.type === 'warn' ? 'warning' : 'error',
        label: eDocScannerErrorMessage.label || '',
        customIcon: undefined,
        icon: true,
      });
    }

    return tags;
  }, [operationChecker, timestampChecker, t, eDocScannerErrorMessage]);

  // PAYMENT_REQUEST_SUPPORT_AGENT: append AI Suggest label (additive, flag-gated).
  // When the flag is off this returns `statusTagsInfo` untouched (old behavior).
  const tagsWithAiSuggest = useMemo<StatusTagInfo[]>(() => {
    if (!aiSuggestChecker.isEnabled) {
      return statusTagsInfo;
    }

    // AI Suggest processing — placed behind the OCR and timestamp tags.
    if (aiSuggestChecker.isProcessing) {
      return [
        ...statusTagsInfo,
        {
          status: 'success',
          label: t('received_invoice.ai_suggest_processing_label'),
          customIcon: null, // Will be added in FileNameCell
        },
      ];
    }

    if (!hasAiSuggestResult(aiSuggestStatus)) {
      return statusTagsInfo;
    }

    const success = isAiSuggestSuccess(aiSuggestStatus);
    return [
      ...statusTagsInfo,
      {
        status: success ? 'success' : 'warning',
        label: t('received_invoice.ai_suggest_label'),
        iconType: success ? 'ai' : 'caution',
        tooltip: t(
          success
            ? 'received_invoice.ai_suggest_tooltip_success'
            : 'received_invoice.ai_suggest_tooltip_failed'
        ),
      },
    ];
  }, [
    statusTagsInfo,
    aiSuggestChecker.isEnabled,
    aiSuggestChecker.isProcessing,
    aiSuggestStatus,
    t,
  ]);

  return {
    statusTagsInfo: tagsWithAiSuggest,
  };
};
